import { 
  useQuery, 
  useMutation, 
  useQueryClient, 
  UseQueryOptions,
  UseMutationOptions 
} from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { PaginationParams, FilterParams } from '@/types';

// Generic fetch function
async function fetchData<T>(url: string, params?: Record<string, unknown> | object): Promise<T> {
  return apiClient.get<T>(url, { params: params as Record<string, unknown> });
}

/**
 * Generic hook for GET requests
 */
export function useFetch<T>(
  key: string | (string | number)[],
  url: string,
  params?: Record<string, unknown> | object,
  options?: Omit<UseQueryOptions<T, Error, T>, 'queryKey' | 'queryFn'>
) {
  return useQuery<T, Error>({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn: () => fetchData<T>(url, params),
    ...options,
  });
}

/**
 * Generic hook for paginated requests
 */
export function usePaginatedFetch<T>(
  key: string,
  url: string,
  pagination: PaginationParams,
  filters?: FilterParams,
  options?: Omit<UseQueryOptions<T, Error, T>, 'queryKey' | 'queryFn'>
) {
  const params = {
    page: pagination.page || 1,
    limit: pagination.limit || 20,
    sortBy: pagination.sortBy,
    sortOrder: pagination.sortOrder,
    search: pagination.search,
    ...filters,
  };

  return useQuery<T, Error>({
    queryKey: [key, params],
    queryFn: () => fetchData<T>(url, params),
    ...options,
  });
}

/**
 * Generic hook for POST requests
 */
export function useCreate<TData extends Record<string, unknown>, TResponse = TData>(
  url: string,
  options?: Omit<UseMutationOptions<TResponse, Error, TData>, 'mutationFn'>
) {
  return useMutation<TResponse, Error, TData>({
    mutationFn: (data: TData) => apiClient.post<TResponse>(url, data as Record<string, unknown>),
    onSuccess: () => {
      // Invalidate related queries if needed
    },
    ...options,
  });
}

/**
 * Generic hook for PUT requests
 */
export function useUpdate<TData extends Record<string, unknown>, TResponse = TData>(
  url: string,
  options?: Omit<UseMutationOptions<TResponse, Error, { id: string; data: TData }>, 'mutationFn'>
) {
  return useMutation<TResponse, Error, { id: string; data: TData }>({
    mutationFn: ({ id, data }) => apiClient.put<TResponse>(`${url}/${id}`, data as Record<string, unknown>),
    ...options,
  });
}

/**
 * Generic hook for PATCH requests
 */
export function usePatch<TData, TResponse = TData>(
  url: string,
  options?: Omit<UseMutationOptions<TResponse, Error, { id: string; data: Partial<TData> }>, 'mutationFn'>
) {
  return useMutation<TResponse, Error, { id: string; data: Partial<TData> }>({
    mutationFn: ({ id, data }) => apiClient.patch<TResponse>(`${url}/${id}`, data),
    ...options,
  });
}

/**
 * Generic hook for DELETE requests
 */
export function useDelete<TResponse = void>(
  url: string,
  options?: Omit<UseMutationOptions<TResponse, Error, string>, 'mutationFn'>
) {
  return useMutation<TResponse, Error, string>({
    mutationFn: (id: string) => apiClient.delete<TResponse>(`${url}/${id}`),
    ...options,
  });
}

/**
 * Hook for optimistic updates
 */
export function useOptimisticUpdate<T>(
  queryKey: string | (string | number)[],
  updateFn: (oldData: T, newData: Partial<T>) => T
) {
  const queryClient = useQueryClient();

  const onMutate = async (newData: Partial<T>) => {
    await queryClient.cancelQueries({ queryKey: Array.isArray(queryKey) ? queryKey : [queryKey] });
    
    const previousData = queryClient.getQueryData<T>(
      Array.isArray(queryKey) ? queryKey : [queryKey]
    );
    
    if (previousData) {
      queryClient.setQueryData(
        Array.isArray(queryKey) ? queryKey : [queryKey],
        updateFn(previousData, newData)
      );
    }
    
    return { previousData };
  };

  const onError = (_err: Error, _newData: Partial<T>, context: { previousData?: T } | undefined) => {
    if (context?.previousData) {
      queryClient.setQueryData(
        Array.isArray(queryKey) ? queryKey : [queryKey],
        context.previousData
      );
    }
  };

  return { onMutate, onError };
}
